import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';

export interface FormNavigationProps {
  onPrevious?: () => void;
  onNext?: () => void;
  onSave?: () => void;
  onSubmit?: () => void;
  onLoadDraft?: () => void;
  onSaveToFile?: () => void;
  showPrevious?: boolean;
  showNext?: boolean;
  showSave?: boolean;
  showSubmit?: boolean;
  showLoadDraft?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  isSubmitting?: boolean;
  isSaving?: boolean;
  nextLabel?: string;
  previousLabel?: string;
  submitLabel?: string;
  saveLabel?: string;
  disabled?: boolean;
}

export const FormNavigation: React.FC<FormNavigationProps> = ({
  onPrevious,
  onNext,
  onSave,
  onSubmit,
  onLoadDraft,
  onSaveToFile,
  showPrevious = true,
  showNext = true,
  showSave = true,
  showSubmit = false,
  showLoadDraft = false,
  isFirstStep = false,
  isLastStep = false,
  isSubmitting = false,
  isSaving = false,
  nextLabel,
  previousLabel,
  submitLabel,
  saveLabel,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const resolvedNextLabel = nextLabel || t('navigation.next');
  const resolvedPreviousLabel = previousLabel || t('navigation.previous');
  const resolvedSubmitLabel = submitLabel || t('common.submit');
  const resolvedSaveLabel = saveLabel || t('navigation.saveDraft');

  return (
    <nav role="navigation" aria-label={t('accessibility.formNavigation')} className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-gray-200 mt-8">
      <div className="flex gap-3 w-full sm:w-auto">
        {showPrevious && !isFirstStep && (
          <Button
            type="button"
            variant="outline"
            onClick={onPrevious}
            disabled={disabled || isSubmitting}
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            }
          >
            {resolvedPreviousLabel}
          </Button>
        )}

        {showSave && showLoadDraft && onLoadDraft ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onLoadDraft}
            disabled={disabled || isSubmitting}
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            }
          >
            {t('navigation.loadDraftFromFile')}
          </Button>
        ) : showSave && onSaveToFile ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onSaveToFile}
            disabled={disabled || isSubmitting}
            isLoading={isSaving}
            leftIcon={
              !isSaving && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )
            }
          >
            {t('navigation.saveDraftToFile')}
          </Button>
        ) : showSave && (
          <Button
            type="button"
            variant="ghost"
            onClick={onSave}
            disabled={disabled || isSubmitting}
            isLoading={isSaving}
            leftIcon={
              !isSaving && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
              )
            }
          >
            {resolvedSaveLabel}
          </Button>
        )}
      </div>

      <div className="flex gap-3 w-full sm:w-auto justify-end">
        {showNext && !isLastStep && (
          <Button
            type="button"
            variant="primary"
            onClick={onNext}
            disabled={disabled || isSubmitting}
            rightIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            }
          >
            {resolvedNextLabel}
          </Button>
        )}

        {showSubmit && isLastStep && (
          <Button
            type="button"
            variant="primary"
            onClick={onSubmit}
            disabled={disabled}
            isLoading={isSubmitting}
            aria-busy={isSubmitting}
            rightIcon={
              !isSubmitting && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )
            }
          >
            {resolvedSubmitLabel}
          </Button>
        )}
      </div>
    </nav>
  );
};

export default FormNavigation;
